import { createSlice } from "@reduxjs/toolkit"
import { find } from "lodash-es"
import {
  downloadFiles,
  testForLoading,
  uploadFiles,
} from "@/core/app/slices/v2/v2apis"

import {
  fetchFileList,
  fetchFileZips,
  forcelockCaseApi,
  getIsReadApi,
  getMessageApi,
  getTreatmentInfos,
  lockCaseApi,
  postMessageApi,
  postModiApi,
  unlockCaseApi,
  updateMessageApi,
  uploadCaseZips,
  initializeWASM,
  fetchZipsFromCacheOrRemote,
} from "./clinicalThunkApi"
import {
  fetchRefinementJsonFile,
  fetchRefinementZipFile,
  undoRefinement
} from "./refinement/refinementThunkApi"
import { fetchZipsAndLoadCase } from "./clinicalworkflow"
import { hasFileHistoryChanged } from "@/modules/Patient/PatientList/util/commonUtil"

export const FILEZIPS = [
  "bulk0",
  `bulk1`,
  `bulk2`,
  `bulk10`,
  "storage.version.json",
  "raw",
  "photo",
]

export type OpenModeType =
  | "setup"
  | "preview"
  | "inpresetup"
  | "needdopresetupbyudesign"
  | null


interface clinicalType {
  /**
   * the case mode for loading
   */
  openMode: OpenModeType
  /**
   * cache the fileinfo list from server
   */
  fileList: object[]
  /**
   * cache all file names
   */
  fileNames: string[]
  /**
   * cache all zip names
   */
  zipNames: string[] | null
  refinementFiles: string[] | null
  refinementZips: object
  curDnZipName: string[] | null
  zipList: object
  treatmentList: []
  fileListReady: boolean
  isShowProgress: boolean
  refinementReady: boolean
  ProgressMsg: string
  messageList: object[]
  historyList: object[]
  isReadMessage: boolean
  isScansRendered: boolean
  caseArch: "both" | "upper" | "lower"
  upperType: "R" | "A" | "--"
  lowerType: "R" | "A" | "--"
  isLoading: "idle" | "pending" | "succeeded" | "failed"
  isWasmLoading: "idle" | "pending" | "succeeded" | "failed"
  /**
   * used to check if the zip file has changed
   * <caseId,fileHistory>
   *  fileHistory: {filename:string,uploadtime:string}[]
   */
  fileHistory: Record<string, { name: string; time: string }[]>
  isCaseFilesUpdated: boolean
  isWasmOpencaseReady: boolean
  isRefinementConverting: boolean
}
const initState: clinicalType = {
  openMode: null,
  fileList: [],
  fileNames: [],
  // v1
  zipNames: [],
  // v2
  // zipNames: null,
  refinementFiles: [],
  refinementZips: {},
  curDnZipName: [],
  zipList: {},
  treatmentList: [],
  isShowProgress: false,
  ProgressMsg: "",
  refinementReady: false,
  fileListReady: false,
  isScansRendered: false,
  messageList: [],
  historyList: [],
  isReadMessage: true,
  caseArch: "both",
  upperType: "--",
  lowerType: "--",
  isLoading: "idle",
  isWasmLoading: "idle",
  fileHistory: {},
  isCaseFilesUpdated: false,
  isWasmOpencaseReady: false,
  isRefinementConverting: false,
}
export const clinicalServiceSlice = createSlice({
  name: "clinicalService",
  initialState: initState,
  reducers: {
    setLoading: (state, action: {payload:"pending"|"succeeded"|"failed"}) =>{
      state.isLoading = action.payload
    },
    setIsScansRendered: (state, action) => {
      state.isScansRendered = action.payload
    },
    setIsShowProgress: (state, action) => {
      state.isShowProgress = action.payload
      if (!state.isShowProgress) {
        state.ProgressMsg = ""
      }
    },
    setProgressMsg: (state, action) => {
      state.ProgressMsg = action.payload
    },
    setRefinementReady: (state, action) => {
      state.refinementReady = action.payload
    },
    resetCase(state) {
      const newState = {
        ...initState,
        
        isScansRendered: state.isScansRendered,
        // v2 don't clear fileHistory to cache the case file history
        fileHistory: state.fileHistory,
        isCaseFilesUpdated: state.isCaseFilesUpdated,
      }
      return newState
    },
    setData(state, { payload }) {
      const newState = {
        ...state,
        ...payload,
      }

      if ("zipNames" in payload) {
        newState.curDnZipName = newState.zipNames
      }

      return newState
    },
    setCurDnZipName(state, action) {
      state.curDnZipName = action.payload
    },
    setOpenMode<s>(state, { payload }: { payload: OpenModeType }) {
      state.openMode = payload
    },
    // New processes dedicated to data cleansing
    cleanUpDataforNew: (state, action) => {
      // clear all
      if (action.payload === "all") {
        state.zipNames = []
        state.curDnZipName = []
        state.zipList = {}
      }
    },
    setIsWasmOpencaseReady: (state, action) => {
      state.isWasmOpencaseReady = action.payload
    },
    cleanRefinementZips: (state) => {
      state.refinementZips = {}
    },
    setIsRefinementConverting: (state, action) => {
      state.isRefinementConverting = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZipsAndLoadCase.pending, (state) => {
      state.isShowProgress = true
    }),
    builder.addCase(fetchZipsAndLoadCase.fulfilled, (state) => {
      if (!state.isRefinementConverting)
        state.isShowProgress = false
    }),
    builder.addCase(fetchZipsAndLoadCase.rejected, (state) => {
      state.isShowProgress = false
    }),
    builder.addCase(
      fetchZipsFromCacheOrRemote.fulfilled,
      (state, action: any) => {
        if (action.error) {
          console.warn("fetchZipsFromCacheOrRemote error", action)
          return
        }
        const payload = action.payload
        state.zipList = { ...payload }
      },
    ),
    builder.addCase(initializeWASM.rejected, (state) => {
      state.isWasmLoading = "failed"
    }),
      builder.addCase(initializeWASM.pending, (state) => {
        state.isWasmLoading = "pending"
      }),
      builder.addCase(initializeWASM.fulfilled, (state) => {
        state.isWasmLoading = "succeeded"
      }),
      // builder.addCase(requestCloudPresetup.rejected,(state)=>{
      //   state.isLoading = "failed"
      // }),
      // builder.addCase(requestCloudPresetup.pending,(state)=>{
      //   state.isLoading = "pending"
      // }),
      // builder.addCase(requestCloudPresetup.fulfilled,(state)=>{
      //   state.isLoading = "complete"
      // }),
      builder.addCase(uploadFiles.pending, (state) => {
        state.isLoading = "pending"
      }),
      builder.addCase(uploadFiles.fulfilled, (state) => {
        state.isLoading = "succeeded"
      }),
      builder.addCase(uploadFiles.rejected, (state) => {
        state.isLoading = "failed"
      }),
      builder.addCase(testForLoading.pending, (state) => {
        state.isLoading = "pending"
      }),
      builder.addCase(downloadFiles.fulfilled, (state, action) => {
        const { payload } = action
        if (payload.status === "Success") {
          for (let i = 0; i < state.zipNames.length; i++) {
            const name = state.zipNames[i] as string
            state.zipList[name] = payload.files[i]
          }
          state.isLoading = "succeeded"
        }
      }),
      builder.addCase(downloadFiles.rejected, (state) => {
        state.isLoading = "failed"
      })
    builder.addCase(downloadFiles.pending, (state) => {
      state.isLoading = "pending"
    })
    builder.addCase(fetchFileList.pending, (state) => {
      state.isLoading = "pending"
    })
    builder.addCase(fetchFileList.fulfilled, (state, action) => {
      if (action.payload.status === "Success") {
        state.fileListReady = true
        state.isLoading = "succeeded"
        const res = action.payload?.result
        if (res?.length) {
          const caseId = action.meta.arg.caseId
          const fileHisotry = state.fileHistory[caseId]
          const filteredFiles = res
            .filter((file) => file.attachment_type === "cases")
            .map((file) => ({
              name: file.file_name,
              time: file.updated_date,
            }))

          if (action.meta.arg.checkFileUpdates) {

            const isCaseFilesUpdated = hasFileHistoryChanged(
              fileHisotry,
              filteredFiles,
            )
            console.log("hasFileHistoryChanged0000",fileHisotry,filteredFiles,isCaseFilesUpdated)
            if (isCaseFilesUpdated === true) {
              state.fileHistory[caseId] = filteredFiles
            }
            state.isCaseFilesUpdated = isCaseFilesUpdated
          }
        }

        const { result } = action.payload
        state.fileNames = []
        // state.zipList = {}
        let error = ""
        const download_fileNames = action.meta.arg?.fileNames || FILEZIPS
        for (const { file_name } of result) {
          state.fileNames.push(file_name)
        }
        state.zipNames = []
        for (const name of download_fileNames) {
          if (!state.fileNames.includes(`${name}.zip`)) {
            error = `not find ${name}.zip`
          } else {
            state.zipNames.push(name)
          }
        }

        state.refinementFiles = []
        if (!state.fileNames.includes(`RetouchHistory.json`)) {
          error = `not find RetouchHistory.json`
        } else {
          state.refinementFiles.push(`RetouchHistory.json`)
        }
      } else {
        // failed, for example, it's empty case.
        console.log("fetchFileList is fullfilled but failed")
        state.isLoading = "failed"
      }
      state.ProgressMsg = "Check file list successfully"
    })
    builder.addCase(fetchFileList.rejected, (state) => {
      console.log("fetchFileList is rejected")
      state.isLoading = "failed"
      state.ProgressMsg = "Check file list failed"
    })
    builder.addCase(fetchFileZips.pending, (state) => {
      state.isLoading = "pending"
      state.ProgressMsg = "Download Case files ..."
    })
    builder.addCase(fetchFileZips.fulfilled, (state, action) => {
      const { payload } = action
      for (let i = 0; i < payload.length; i++) {
        const item = payload[i]
        const data = item.data
        const respUrl = item.request.responseURL
        const fileName = respUrl.split("/").pop()
        state.zipList[fileName] = data
      }
      state.isLoading = "succeeded"
      state.ProgressMsg = "Download Ok ..."
    })
    builder.addCase(fetchFileZips.rejected, (state) => {
      console.log("rejected")
      state.isLoading = "failed"
      state.ProgressMsg = "Download False ..."
    })
    builder.addCase(fetchRefinementJsonFile.fulfilled, (state, action) => {
      const { payload } = action
      for (let i = 0; i < state.refinementFiles.length; i++) {
        const name = state.refinementFiles[i] as string
        state.zipList["RetouchHistory/" + name] = payload[i].data
      }
    })
    builder.addCase(fetchRefinementZipFile.fulfilled, (state, action) => {
      const { payload } = action
      state.refinementZips = []
      for (let i = 0; i < payload.length; i++) {
        state.refinementZips["RetouchHistoryZip" + i] = payload[i].data
      }
    })
    builder.addCase(fetchRefinementZipFile.rejected, (state) => {
      console.log("rejected")
      state.refinementZips = []
    })
    builder.addCase(getTreatmentInfos.pending, (state) => {
    })
    builder.addCase(getTreatmentInfos.fulfilled, (state, action) => {
      const { payload } = action
      state.treatmentList = payload.result
    })
    builder.addCase(getTreatmentInfos.rejected, (state) => {
      console.log("rejected")
    })
    builder.addCase(uploadCaseZips.pending, (state) => {
      state.isLoading = "pending"
    })
    builder.addCase(uploadCaseZips.fulfilled, (state, action) => {
      const {
        payload,
        meta: {
          arg: { callback, zips },
        },
      } = action
      const zipList = {}
      for (const { file, fileName } of zips) {
        zipList[fileName] = file
      }
      if (zips.length !== 1 && zips.length !== 2) {
        state.zipList = zipList
      }

      const filteredFiles = payload.map((item) => ({
        name: item.data.result.file_name,
        time: item.data.result.updated_date,
      }))

      const caseId = action.meta.arg.caseId
      const fileHisotry = state.fileHistory[caseId]
      const isCaseUpdated = hasFileHistoryChanged(
        fileHisotry,
        filteredFiles,
      )
      if (isCaseUpdated && fileHisotry) {
        const updatedFilesMap = new Map(
          filteredFiles.map((item) => [item.name, item]),
        )

        const newFileHistory = [
          ...fileHisotry.map(file => updatedFilesMap.get(file.name) ?? file),
          ...Array.from(updatedFilesMap.values()).filter(
            updated => !fileHisotry.some(f => f.name === updated.name)
          )
        ]
        state.fileHistory[caseId] = newFileHistory
        
      }
      state.isLoading = "succeeded"
      callback && callback()
    })
    builder.addCase(uploadCaseZips.rejected, (state) => {
      console.log("rejected")
      state.isLoading = "failed"
    })
    builder.addCase(postMessageApi.fulfilled, (state, action) => {
      const {
        payload: { result, status },
        meta: { arg: cb },
      } = action
      setTimeout(() => {
        cb?.cb && cb.cb()
      }, 0)
    })
    builder.addCase(postMessageApi.rejected, (state) => {
      console.log("rejected")
    })
    builder.addCase(postModiApi.fulfilled, (state, action) => {
      const {
        payload: { data },
        meta: { arg: cb },
      } = action

      setTimeout(() => {
        cb?.cb && cb.cb()
      }, 0)
    })
    builder.addCase(postModiApi.rejected, (state) => {
      console.log("rejected")
    })
    builder.addCase(getMessageApi.fulfilled, (state, action) => {
      const {
        payload: {
          results: {
            current_thread,
            previous_thread,
            users_avatar,
            unread_messages,
          },
          status,
        },
      } = action
      const current = [],
        previous = []

      current_thread &&
        current_thread.map(
          ({
            created_at = "",
            body,
            message_type,
            id,
            plan_name,
            created_by,
            button_enable,
          }) => {
            const {
              subject = "",
              content = "",
              // sender: { firstname, lastname, avatar_link, avatar_color, title },
            } = JSON.parse(body)

            let firstname = "", lastname = "", avatar_link = "", avatar_color = "", title = ""
            // find user info by created_by if created_by is 0, it's system message
            if (created_by !== 0) {
              const userInfo = find(users_avatar, ["id", created_by])
              if (userInfo) {
                firstname = userInfo?.first_name
                lastname = userInfo?.last_name
                avatar_link = userInfo?.avatar_link
                avatar_color = userInfo?.avatar_color
                title = userInfo?.title_name?.type_name
              }
            }

            current.push({
              firstname,
              lastname,
              time: created_at,
              photo: avatar_link === "-" ? "" : avatar_link,
              color: avatar_color,
              role: title.toUpperCase(),
              messageStatus: message_type,
              message: { title: plan_name, text: content, id, subject },
              button_enable: button_enable,
            })
          },
        )
      previous_thread &&
        previous_thread.map(
          ({ created_at = "", body, message_type, id, plan_name }) => {
            const {
              subject = "",
              content = "",
              sender: { firstname, lastname, avatar_link, avatar_color, title },
            } = JSON.parse(body)
            previous.push({
              firstname,
              lastname,
              time: created_at,
              photo: avatar_link === "-" ? "" : avatar_link,
              color: avatar_color,
              role: title.toUpperCase(),
              messageStatus: message_type,
              message: { title: plan_name, text: content, id, subject },
            })
          },
        )
      state.messageList = [...current]
      state.historyList = [...previous]
    })
    builder.addCase(getMessageApi.rejected, (state) => {
      console.log("rejected")
    })
    builder.addCase(getIsReadApi.fulfilled, (state, action) => {
      const {
        payload: {
          results: { unread_message = false },
        },
      } = action
      state.isReadMessage = !unread_message
    })
    builder.addCase(getIsReadApi.rejected, (state) => {
      console.log("rejected")
    })
    builder.addCase(lockCaseApi.pending, (state) => {
      state.isLoading = "pending"
    })
    builder.addCase(lockCaseApi.fulfilled, (state, action) => {
      state.isLoading = "succeeded"
    })
    builder.addCase(lockCaseApi.rejected, (state) => {
      console.log("rejected")
      state.isLoading = "failed"
    })
    builder.addCase(unlockCaseApi.pending, (state) => {
      state.isLoading = "pending"
    })
    builder.addCase(unlockCaseApi.fulfilled, (state, action) => {
      const { payload } = action
      state.isLoading = "succeeded"
    })
    builder.addCase(unlockCaseApi.rejected, (state) => {
      console.log("rejected")
      state.isLoading = "failed"
    })
    builder.addCase(forcelockCaseApi.pending, (state) => {
      state.isLoading = "pending"
    })
    builder.addCase(forcelockCaseApi.fulfilled, (state, action) => {
      const { payload } = action
      state.isLoading = "succeeded"
    })
    builder.addCase(forcelockCaseApi.rejected, (state) => {
      console.log("rejected")
      state.isLoading = "failed"
    })
    builder.addCase(updateMessageApi.rejected, (state) => {
      state.isLoading = "failed"
    })
    builder.addCase(updateMessageApi.fulfilled, (state, action) => {
      state.isLoading = "succeeded"
      const {
        meta: { arg: cb },
      } = action

      setTimeout(() => {
        cb?.cb && cb.cb()
      }, 0)
    })
    builder.addCase(updateMessageApi.pending, (state) => {
      state.isLoading = "pending"
    })
  },
})
export const {
  setLoading,
  resetCase,
  setCurDnZipName,
  setIsScansRendered,
  setIsShowProgress,
  setProgressMsg,
  setRefinementReady,
  setData: setClinicalData,
  setOpenMode,
  cleanUpDataforNew,
  cleanRefinementZips,
  setIsWasmOpencaseReady,
  setIsRefinementConverting,
} = clinicalServiceSlice.actions
